import React, { useEffect, useState } from "react";
import { MsalProvider } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "./authConfig";
import Dashboard from "./Pages/Dashboard";
import Layout from "./components/Layout";
import SupervisorListPage from "./Pages/SupervisorListPage";
import EmployeeListPage from "./Pages/EmployeeListPage";
import TimesheetPage from "./Pages/TimesheetPage";
import { SiteContextProvider, useSiteContext } from "./SiteContext";
import PaycorDownload from "./Pages/wcdp/paycor/PaycorDownload";
import './App.css';
import { Page } from "./interfaces/PagesInterface";
import Popup from "./components/Popup";
import ManualPaycor from "./Pages/wcdp/paycor/ManualPaycor";
import V1SupervisorListPage from "./Pages/wcdp/V1Pages/V1SupervisorListPage";
import V1EmployeeListPage from "./Pages/wcdp/V1Pages/V1EmployeeListPage";
import './styles/common.css';
import WcdpProjectImport from "./Pages/wcdp/projectImport/WcdpProjectImport";
import { WcdpImportVariantKey } from "./Pages/wcdp/projectImport/wcdpImportVariants";
import EmployeeView from "./Pages/EmployeeView";
import EmployeeViewV1 from "./Pages/EmployeeViewV1";
import PayPeriodsPage from "./Pages/PayPeriods";
import EmployeeRolesPage from "./Pages/v2/employeeRoles/EmployeeRolesPage";
import AdminEmployeeView from "./Pages/AdminEmployeeView";
import LocationsPage from "./Pages/LocationsPage";
import AccountCodePage from "./Pages/AccountCodePage";
import V1BatchExport from "./Pages/wcdp/V1Pages/V1BatchExport";
import V2BatchExport from "./Pages/wcdp/V2Pages/V2BatchExport";
import VistaSupervisorListPage from "./Pages/npmech/VistaSupervisorListPage";
import PaycorDownloadV2 from "./Pages/wcdp/paycor/PaycorDownloadV2";
import PaycorPrdaLocal from "./Pages/wcdp/paycor/PaycorPrdaLocal";
import PrdaMonthlyReportsPage from "./Pages/v2/PrdaMonthlyReportsPage";
import MiscellaneousTasksPage from "./Pages/MiscellaneousTasksPage";
import MiscTaskApprovalPage from "./Pages/v2/miscTasks/MiscTaskApprovalPage";
import ReportsPage from "./Pages/ReportsPage";
import ModifiedDutyPage from "./Pages/v2/ModifiedDutyPage";
import ProjectsPage from "./Pages/ProjectsPage";
import ProjectsPageV1 from "./Pages/ProjectsPageV1";
import CommentsPage from "./Pages/CommentsPage";
import TimesheetExceptionsPage from "./Pages/v2/TimesheetExceptionsPage";
import SickHoursPage from "./Pages/v2/SickHoursPage";
import LaSickPage from "./Pages/v2/LaSickPage";
import AuditReportPage from "./Pages/AuditReportPage";
import ReportsHubPage from "./Pages/v2/ReportsHubPage";
import AttendanceReportPage from "./Pages/AttendanceReportPage";
import TimesheetDeadlineReportPage from "./Pages/TimesheetDeadlineReportPage";
import WeeklyOpsReportPage from "./Pages/WeeklyOpsReportPage";
import PieceImport from "./Pages/2020ph/PieceImport";
import WorkOrdersPage from "./Pages/v2/workOrders/WorkOrdersPage";
import WorkOrderDetailPage from "./Pages/v2/workOrders/WorkOrderDetailPage";
import ChangeOrdersPage from "./Pages/v2/workOrders/ChangeOrdersPage";
import CustomerContactsPage from "./Pages/v2/workOrders/CustomerContactsPage";
import ChangeOrderCreatePage from "./Pages/v2/workOrders/ChangeOrderCreatePage";
import ChangeOrderDetailPage from "./Pages/v2/workOrders/ChangeOrderDetailPage";
import RiceServicesPieceImport from "./Pages/npmech/RiceServicesPieceImport";
import PWFringeTypesPage from "./Pages/PWFringeTypesPage";
import PWClassificationsPage from "./Pages/PWClassificationsPage";
import PWProjectSetupPage from "./Pages/PWProjectSetupPage";
import SyncStatusPage from "./Pages/SyncStatusPage";
import SelfieExceptionsPage from "./Pages/v2/SelfieExceptionsPage";
import TimeOffConfigurationPage from "./Pages/TimeOffConfigurationPage";
import ProcessPayrollPage from "./Pages/ProcessPayrollPage";
import UnionWagesPage from "./Pages/UnionWagesPage";
import ProjectMapPage from "./Pages/v2/ProjectMapPage";
import TimeOffRequestApprovalPage from "./Pages/TimeOffRequestApprovalPage";
import TimesheetReview2Page from "./Pages/TimesheetReview2Page";
import ProjectWorkPage from "./Pages/ProjectWorkPage";
import CrewWorkPage from "./Pages/v2/CrewWorkPage";
import config from "./configLoader";
import LayoutV2 from "./components/v2/LayoutV2";
import HomeV2 from "./Pages/v2/HomeV2";
import TimesheetReviewBoardPage from "./Pages/v2/TimesheetReviewBoardPage";
import PayrollTimesheetEditPage from "./Pages/v2/PayrollTimesheetEditPage";
import GpsExceptionsPage from "./Pages/v2/GpsExceptionsPage";
import ReportsConfigPage from "./Pages/v2/ReportsConfigPage";
import SafetyMessagePage from "./Pages/v2/SafetyMessagePage";
import KioskDevicesPage from "./Pages/v2/KioskDevicesPage";
import AdminManagementPage from "./Pages/v2/AdminManagementPage";
import TodayPage from "./Pages/v2/employeePortal/TodayPage";
import MyActivityPage from "./Pages/v2/employeePortal/MyActivityPage";
import MyTimeOffPage from "./Pages/v2/employeePortal/MyTimeOffPage";
import MyTimesheetPage from "./Pages/v2/employeePortal/MyTimesheetPage";
import {darkenColor} from "./util/ColorUtil";
import { parseDeepLinkParams, stashDeepLink } from "./interfaces/DeepLinkInterface";
import { can, canEditEntries, canViewWorkOrders as roleCanViewWorkOrders } from "./util/adminPermissions";

export default function App() {
  return (
    <SiteContextProvider>
      <AppContent />
    </SiteContextProvider>
  );
}

const msalInstance = new PublicClientApplication(msalConfig);

function AppContent() {
  const [error, setError] = useState<string | null>(null);
  const { loading, userID, userName, page, popupProps, userRole, setPendingDeepLink, reloadNonce, employeeMode } = useSiteContext();
  const [currentPage, setCurrentPage] = useState<string>(page); // track the current page in state

  const primaryColor = config.primaryColor;
  const secondaryColor = config.secondaryColor;
  const tertiaryColor = config.tertiaryColor;

  // Parse deep link params from URL on mount (survives MSAL loginPopup)
  useEffect(() => {
    const params = parseDeepLinkParams();
    if (params.page || params.requestId || params.exceptionId || params.eventId) {
      setPendingDeepLink(params);
      // Also stashed module-side: useAdminSession clears pendingDeepLink in the
      // same effect that sets the page, so a target page can mount after the
      // context value is already null. See DeepLinkInterface.stashDeepLink.
      stashDeepLink(params);
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
      if (primaryColor) {
        document.documentElement.style.setProperty('--primary-color', primaryColor);
        document.documentElement.style.setProperty('--primary-color-hover', darkenColor(primaryColor, 15));
        document.documentElement.style.setProperty('--secondary-color', secondaryColor);
        document.documentElement.style.setProperty('--tertiary-color', tertiaryColor);
        document.documentElement.style.setProperty('--tertiary-color-hover', darkenColor(tertiaryColor, 5));
      }
    }, [primaryColor]);  

  useEffect(() => {
    console.log("User: ", userName);
    console.log("ID: ", userID);
  }, [userID, userName]);

  useEffect(() => {
    navigateTo(page);
  }, [page]);

  // Function to change the current page (without modifying URL)
  const navigateTo = (page: string) => {
    setCurrentPage(page); // This will update the component being displayed
  };

  // Home is the shared landing + guard fallback for every gated page.
  // Under version2 the redesigned HomeV2 replaces the legacy Dashboard.
  const renderHome = () =>
    config.version2 ? <HomeV2 /> : <Dashboard setError={setError} />;

  // The five WCDP import routes share one page. The guard matches the nav
  // gating in navConfig.tsx / Layout.tsx (and the Page.PieceImport precedent),
  // which the old unguarded routes were missing.
  const renderWcdpImport = (variant: WcdpImportVariantKey) => {
    if (!can(userRole, 'importTasks') || !config.allowWCDPProjectImport) {
      return renderHome();
    }
    return <WcdpProjectImport variant={variant} />;
  };

  // Rendering different components based on the currentPage state
  // Employee Time Portal (P-TAS-20 Phase 3): routed FIRST. In the employee experience every
  // page is a My Time page and the default is Today — never renderHome(), whose HomeV2 calls
  // /admin/*. Outside it, the My Time pages fall through to the default (Home).
  const renderPortalPage = () => {
    switch (currentPage) {
      case Page.MyTimesheet:
        return <MyTimesheetPage />;
      case Page.MyTimeOff:
        return <MyTimeOffPage />;
      case Page.MyActivity:
        return <MyActivityPage />;
      case Page.MyToday:
      default:
        return <TodayPage />;
    }
  };

  const renderPage = () => {
    if (employeeMode) return renderPortalPage();
    const canViewWorkOrders = roleCanViewWorkOrders(userRole);
    switch (currentPage) {
      case Page.Dashboard:
        return renderHome();
      case Page.DashboardOld:
        // Always the legacy v1 Home, even under version2 (fallback comparison link).
        return <Dashboard setError={setError} />;
      case Page.SupervisorList:
        return <SupervisorListPage setError={setError} />;
      case Page.EmployeeList:
        return <EmployeeListPage setError={setError} />;
      case Page.TimeSheetPage:
        return <TimesheetPage />;
      case Page.PaycorDownload:
        return <PaycorDownload setError={setError} />;
      case Page.ManualPaycor:
        return <ManualPaycor setError={setError} />;
      case Page.V1Supervisors:
        return <V1SupervisorListPage setError={setError} />;
      case Page.V1Employees:
        return <V1EmployeeListPage setError={setError} />;
      case Page.WCDPPaintSingleFamilyImport:
        return renderWcdpImport('paintSF');
      case Page.WCDPPaintMultiFamilyImport:
        return renderWcdpImport('paintMF');
      case Page.WCDPPaintMultiFamilyImportV1:
        return renderWcdpImport('paintMFV1');
      case Page.WCDPDrywallSingleFamilyImport:
        return renderWcdpImport('drywallSF');
      case Page.WCDPDrywallMultiFamilyImport:
        return renderWcdpImport('drywallMF');
      case Page.UPFPieceBudgetImport:
        // UPF reuses the shared import page but has its own tenant flag,
        // separate from allowWCDPProjectImport.
        if (!can(userRole, 'importTasks') || !config.allowUpfPieceImport) {
          return renderHome();
        }
        return <WcdpProjectImport variant="upfPiecePdf" />;
      case Page.EmployeeView:
        return config.version2 ? <EmployeeView /> : <EmployeeViewV1 />;
      case Page.PayPeriods:
      return <PayPeriodsPage />;
      case Page.RolePage:
        // Employee Roles (TAS-668): Manage employee roles edits; Edit employee role
        // gets the read-only view; anyone else is sent Home (deep-link safe).
        if (can(userRole, 'manageEmployeeRoles')) return <EmployeeRolesPage readOnly={false} />;
        if (can(userRole, 'editEmployeeRole')) return <EmployeeRolesPage readOnly />;
        return renderHome();
      case Page.AdminEmployeeView:
        return <AdminEmployeeView />;
      case Page.AdminManagement:
        // Super-admin-only page (D16). Deep-link-safe: non-super-admins are
        // redirected to Home. v2-only surface (config.version2 shell).
        if (!userRole?.admin || !config.version2) {
          return renderHome();
        }
        return <AdminManagementPage />;
      case Page.Locations:
        return <LocationsPage />;
      case Page.AccountCode:
        return <AccountCodePage />;
      case Page.V1BatchExport:
        return <V1BatchExport setError={setError} />;
      case Page.V2BatchExport:
        // Hidden behind the board's IAS Export (TAS-682); the code stays until Phase 2.
        if (config.showWcdpBatchExport) return null;
        return <V2BatchExport setError={setError} />;
      case Page.VistaExport:
        return <VistaSupervisorListPage setError={setError}/>
      case Page.PaycorDownloadV2:
        // Hidden behind the board's Generate Paycor File (TAS-682).
        if (config.showWcdpBatchExport) return null;
        return <PaycorDownloadV2 setError={setError} />;
      case Page.PaycorPrdaLocal:
        if (!(config.showPrdaLocal ?? config.allowV1Export)) return null;
        return <PaycorPrdaLocal setError={setError} />;
      case Page.PrdaMonthlyReports:
        if (!config.showPrdaMonthlyReportsPage || !can(userRole, 'exportPayroll')) {
          return renderHome();
        }
        return <PrdaMonthlyReportsPage />;
      case Page.MiscellaneousTasks:
        // The route was previously unguarded while the nav item was gated; the
        // guard now matches navConfig / Layout so a deep link cannot get past it.
        if (!can(userRole, 'viewMiscTasks') || !config.showMiscTasksPage) {
          return renderHome();
        }
        // NPM Reds two-level approval (TAS-358). Config-gated on its own flag,
        // NOT on version2: NPM production is already version2, and the v2 page
        // needs the matching server flag on or /counts 404s. Both flip together.
        return config.miscTaskTwoLevelApproval === true
          ? <MiscTaskApprovalPage />
          : <MiscellaneousTasksPage />;
      case Page.Reports:
        return <ReportsPage />;
      case Page.ModifiedDuty:
        if (!can(userRole, 'viewModifiedDuty') || !config.showModifiedDuty) {
          return renderHome();
        }
        return <ModifiedDutyPage />;
      case Page.Projects:
        if (!config.showProjectsPage || !userRole?.viewProjectsPage) {
          return renderHome();
        }
        return config.version2 ? <ProjectsPage /> : <ProjectsPageV1 />;
      case Page.Comments:
        if (!config.showCommentsPage || !can(userRole, 'viewTimesheetComments')) {
          return renderHome();
        }
        return <CommentsPage />;
      case Page.TimesheetExceptions:
        // Reuses viewTimesheetComments deliberately — no AdminEmployeeRole
        // migration across six databases, and everyone who can see comments today
        // can see exceptions tomorrow, which is the intent.
        if (!config.showTimesheetExceptionsPage || !can(userRole, 'viewTimesheetComments')) {
          return renderHome();
        }
        return <TimesheetExceptionsPage />;
      case Page.SickHours:
        if (!can(userRole, 'payrollApproveTimesheets') || !config.showSickHoursPage) {
          return renderHome();
        }
        return <SickHoursPage />;
      case Page.LaSick:
        // NPM only (showLaSickPage) + the View LA Sick role permission; super admin passes via can().
        if (!config.showLaSickPage || !can(userRole, 'viewLASick')) {
          return renderHome();
        }
        return <LaSickPage />;
      case Page.ReportsHub:
        if (!config.showReportsHubPage || !can(userRole, 'viewReports')) {
          return renderHome();
        }
        return <ReportsHubPage />;
      case Page.AuditReport:
        if (!config.showAuditReportPage || !can(userRole, 'viewReports')) {
          return renderHome();
        }
        return <AuditReportPage />;
      case Page.AttendanceReport:
        if (!config.showAuditReportPage || !can(userRole, 'viewReports')) {
          return renderHome();
        }
        return <AttendanceReportPage />;
      case Page.TimesheetDeadlineReport:
        if (!config.showRiceServicesReports || !can(userRole, 'viewReports')) {
          return renderHome();
        }
        return <TimesheetDeadlineReportPage />;
      case Page.WeeklyOpsReport:
        if (!config.showRiceServicesReports || !can(userRole, 'viewReports')) {
          return renderHome();
        }
        return <WeeklyOpsReportPage />;
      case Page.PieceImport:
        if (!can(userRole, 'importTasks') || !config.allow2020PieceImport) {
          return renderHome();
        }
        return <PieceImport />;
      case Page.WorkOrders:
        if (!config.showWorkOrdersPage) {
          return <Dashboard setError={setError} />;
        }
        if (!canViewWorkOrders) {
          return <Dashboard setError={setError} />;
        }
        return <WorkOrdersPage />;
      case Page.WorkOrderDetail:
        if (!config.showWorkOrdersPage) {
          return <Dashboard setError={setError} />;
        }
        if (!canViewWorkOrders) {
          return <Dashboard setError={setError} />;
        }
        return <WorkOrderDetailPage />;
      case Page.CustomerContacts:
        if (!config.showWorkOrdersPage || !canViewWorkOrders) {
          return <Dashboard setError={setError} />;
        }
        return <CustomerContactsPage />;
      case Page.ChangeOrders:
        if (!config.showChangeOrdersPage) {
          return <Dashboard setError={setError} />;
        }
        if (!canViewWorkOrders) {
          return <Dashboard setError={setError} />;
        }
        return <ChangeOrdersPage />;
      case Page.ChangeOrderCreate:
        if (!config.showChangeOrdersPage) {
          return <Dashboard setError={setError} />;
        }
        if (!canViewWorkOrders) {
          return <Dashboard setError={setError} />;
        }
        return <ChangeOrderCreatePage />;
      case Page.ChangeOrderDetail:
        if (!config.showChangeOrdersPage) {
          return <Dashboard setError={setError} />;
        }
        if (!canViewWorkOrders) {
          return <Dashboard setError={setError} />;
        }
        return <ChangeOrderDetailPage />;
      case Page.RiceServicesPieceImport:
        if (!can(userRole, 'importTasks') || !config.allowRiceServicesPieceImport) {
          return renderHome();
        }
        return <RiceServicesPieceImport />;
      case Page.PWFringeTypes:
        if (!userRole?.admin || !config.showPrevailingWagePages) {
          return renderHome();
        }
        return <PWFringeTypesPage />;
      case Page.PWClassifications:
        if (!userRole?.admin || !config.showPrevailingWagePages) {
          return renderHome();
        }
        return <PWClassificationsPage />;
      case Page.PWProjectSetup:
        if (!userRole?.admin || !config.showPrevailingWagePages) {
          return renderHome();
        }
        return <PWProjectSetupPage />;
      case Page.SyncStatus:
        if (!userRole?.admin || !config.showSyncStatusPage) {
          return renderHome();
        }
        return <SyncStatusPage />;
      case Page.SelfieVerificationExceptions:
        if (!config.showPhotoPages || !can(userRole, 'viewSelfieExceptions')) {
          return renderHome();
        }
        // v2-styled page; renders under both shells (NPMech, the only tenant
        // with showPhotoPages, is still on the v1 shell).
        return <SelfieExceptionsPage />;
      case Page.TimeOffConfiguration:
        if (!userRole?.admin || !config.showTimeOffConfig) {
          return renderHome();
        }
        return <TimeOffConfigurationPage />;
      case Page.ProcessPayroll:
        if (!can(userRole, 'processPayroll') || !config.showProcessPayroll) {
          return renderHome();
        }
        return <ProcessPayrollPage />;
      case Page.UnionWages:
        if (!userRole?.admin || !config.showUnionClassificationPages) {
          return renderHome();
        }
        return <UnionWagesPage />;
      case Page.ProjectMap:
        if (!userRole?.viewAllProjectsMap) {
          return renderHome();
        }
        return <ProjectMapPage />;
      case Page.TimeOffRequestApproval:
        if (!config.showTimeOffApprovalPage || !can(userRole, 'viewTimeOffRequests')) {
          return renderHome();
        }
        return <TimeOffRequestApprovalPage />;
      case Page.ProjectWork:
        if (!config.showProjectWorkPage || !can(userRole, 'viewProjectWork')) {
          return renderHome();
        }
        return <ProjectWorkPage />;
      case Page.CrewWork:
        if (!config.showCrewWorkPage || !can(userRole, 'viewProjectWork')) {
          return renderHome();
        }
        return <CrewWorkPage />;
      case Page.TimesheetReview:
        if (!config.showTimesheetReviewPage || !can(userRole, 'viewTimesheets')) {
          return renderHome();
        }
        return config.version2 ? <TimesheetReviewBoardPage /> : <TimesheetReview2Page />;
      case Page.PayrollTimesheetEdit:
        // Payroll Timesheet Modifications (M1-B3): v2-only route stub for the M2
        // editor — feature flag + EditTimesheetEntries (admin implies it).
        if (!config.version2 || config.payrollTimesheetEditing?.enabled !== true || !canEditEntries(userRole)) {
          return renderHome();
        }
        return <PayrollTimesheetEditPage />;
      case Page.GpsExceptions:
        if (!config.showGpsExceptions || !can(userRole, 'viewGpsExceptions')) {
          return renderHome();
        }
        return <GpsExceptionsPage />;
      case Page.ReportsConfiguration:
        if (!config.showReportsConfigPage || !userRole?.admin) {
          return renderHome();
        }
        return <ReportsConfigPage />;
      case Page.SafetyMessage:
        if (!config.showSafetyMessagePage || !can(userRole, 'manageSafetyMessages')) {
          return renderHome();
        }
        return <SafetyMessagePage />;
      case Page.KioskDevices:
        if (!config.showKioskDevicesPage || !can(userRole, 'manageKioskDevices')) {
          return renderHome();
        }
        return <KioskDevicesPage />;
      default:
        return renderHome();
    }
  };

  return (
    <MsalProvider instance={msalInstance}>
      <div>
        {loading && (
          <div className="loadingOverlay">
            <div className="overlay" />
            <img
              src={require('./styles/images/loading.gif')}
              alt="Loading..."
              className="loadingImage"
            />
          </div>
        )}

        {/* Modal JSX */}
        <Popup
          isModalVisible={popupProps.isModalVisible}
          closeModal={popupProps.closeModal}
          popupTitle={popupProps.popupTitle}
          popupMessage={popupProps.popupMessage}
        />

        {error && (
          <div className="errorMessage">
            <p>{error}</p>
          </div>
        )}

        {config.version2 ? (
          <LayoutV2>
            <React.Fragment key={`${currentPage}-${reloadNonce}`}>{renderPage()}</React.Fragment>
          </LayoutV2>
        ) : (
          <Layout>{renderPage()}</Layout>
        )}
      </div>
    </MsalProvider>
  );
}
